import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { LANDING_PAGES } from "../pages/landing/landingPages.js"

const STORAGE_KEY = "enabledLandingPages"

function getEnabledKeys() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function groupForPath(path = "") {
  if (path.includes("/websites-for-")) return "Business segments"
  if (path.includes("google") || path.includes("maps") || path.includes("search-console")) return "Google visibility"
  if (path.includes("speed") || path.includes("pagespeed")) return "Speed and performance"
  if (path.includes("form")) return "Forms and enquiries"
  if (path.includes("pay-") || path.includes("no-payment") || path.includes("build-now")) return "Payment options"
  return "Problem solving"
}

export default function HomeLandingLinks({
  title = "Common website problems we fix",
  lead = "Pick what sounds like your situation. Each page explains the problem and the fastest way to fix it.",
  max = 12,
  showGroups = false,
}) {
  const [enabledKeys, setEnabledKeys] = useState([])

  useEffect(() => {
    if (typeof window === "undefined") return

    const sync = () => setEnabledKeys(getEnabledKeys())
    sync()

    window.addEventListener("storage", sync)
    return () => window.removeEventListener("storage", sync)
  }, [])

  const enabledPages = useMemo(() => {
    const keys = new Set(enabledKeys)
    const pages = LANDING_PAGES.filter((p) => p?.key && p?.path && keys.has(p.key))
      .map((p) => ({
        key: p.key,
        path: p.path,
        label: p.navLabel || p.shortTitle || p.heroHeading || p.title || p.path,
        group: groupForPath(p.path),
      }))

    pages.sort((a, b) => {
      if (showGroups) return (a.group || "").localeCompare(b.group || "") || (a.path || "").localeCompare(b.path || "")
      return (a.path || "").localeCompare(b.path || "")
    })

    return pages.slice(0, max)
  }, [enabledKeys, max, showGroups])

  if (!enabledPages.length) return null

  return (
    <section className="max-w-5xl mx-auto px-4 py-12">
      <div className="bg-blue-50 border border-blue-100 rounded-3xl p-8">
        <div className="max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-blue-900">{title}</h2>
          {lead ? <p className="text-gray-700 mt-3">{lead}</p> : null}
        </div>

        <div className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {enabledPages.map((p) => (
              <Link
                key={p.key}
                to={p.path}
                className="bg-white border border-blue-100 rounded-2xl px-4 py-4 shadow-sm hover:shadow-md transition"
              >
                {showGroups ? (
                  <p className="text-xs font-semibold text-blue-700 mb-1">{p.group}</p>
                ) : null}
                <p className="text-sm font-semibold text-gray-900">{p.label}</p>
                <p className="text-xs text-gray-500 mt-1">{p.path}</p>
              </Link>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition"
            >
              Book a Free Website Health Check
            </Link>
            <Link
              to="/contact"
              className="inline-block text-blue-700 font-medium hover:underline px-2 py-3"
            >
              Ask a quick question →
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}